import React from 'react'
import Styles from './BrandPlanCurrentVolumeV2.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Formik } from 'formik'
import { number, object } from 'yup'
import { useMemo, useState } from 'react'
import { useUpdateBrandPlan, Variables } from 'hooks/queries/useUpdateBrandPlan'
import { BrandPlanTactic } from 'types'
import { generatePath, useHistory } from 'react-router-dom'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useOnboard } from 'hooks/queries/useOnboard'
import dayjs from 'dayjs'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import store from 'redux/store'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { workspacesSet } from 'redux/actions'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useStartBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { Form } from 'components/FirstTimeExperienceModal/components/Form/Form'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { BrandPlanCurrentVolumeProps } from './types'
import { TacticIntegrationList } from 'components/TacticIntegrationList/TacticIntegrationList'
import { TacticIntegrationTopBar } from 'components/TacticIntegrationList/TacticIntegrationTopBar'

export const BrandPlanCurrentVolumeV2 = ({
  brandPlan,
  onBack,
  onNext,
  selectedBlogPlatform: propSelectedBlogPlatform,
  selectedEmailPlatform: propSelectedEmailPlatform,
}: BrandPlanCurrentVolumeProps): React.ReactElement => {
  const [isDirty, setIsDirty] = useState(false)
  const { mutate: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan.id })
  const tactics = brandPlan.tactics
  const history = useHistory()
  const { mutateAsync: onboard } = useOnboard()
  const { mutateAsync: startBrandPlan, isLoading: isGenerating } = useStartBrandPlan({ brandPlanId: brandPlan.id })
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspaces = useWorkspaces()
  const { trackGenerateContentClicked } = useOnboardingTracking()
  const { data: integrations } = useExternalIntegrations()

  const selectedBlogPlatform = propSelectedBlogPlatform || brandPlan.selected_blog_platform
  const selectedEmailPlatform = propSelectedEmailPlatform || brandPlan.selected_email_platform

  const markVirtualMarketerOnboardingCompleted = async () => {
    await WorkspaceClient.completeVirtualMarketerOnboarding(currentWorkspaceId)
    store.dispatch(
      workspacesSet(
        workspaces.map((ws) =>
          ws.id === currentWorkspaceId ? { ...ws, is_virtual_marketer_onboarding_completed: true } : ws,
        ),
      ),
    )
  }

  const schema = useMemo(() => {
    let schema = object({})

    if (tactics.includes(BrandPlanTactic.Email)) {
      schema = schema.concat(
        object({
          email_avg_open_rate: number().required(''),
          email_avg_click_through_rate: number().required(''),
        }),
      )
    }

    return schema
  }, [tactics])

  const handleSubmit = async (values: Variables) => {
    // Track the generate content button click
    trackGenerateContentClicked()

    updateBrandPlan(values, {
      onSuccess: async () => {
        try {
          const { first_schedule: schedule } = await startBrandPlan({})
          await onboard()
          await markVirtualMarketerOnboardingCompleted()

          if (schedule?.id) {
            const pathname = generatePath(SCHEDULED_POSTS_PATH, {
              workspaceId: brandPlan.workspace_id,
              id: schedule.id,
            })
            const search = `?date=${dayjs(schedule.start_date).format('YYYY-MM-DD')}&load-tutorial=true`
            history.replace({ pathname, search })
          } else {
            showErrorMessageToast('Failed to generate content, please try again.')
          }
        } catch (error) {
          showErrorMessageToast('Failed to generate content, please try again.')
        }
      },
    })
  }

  const numConnectedAccounts = integrations?.external_integrations?.length || 0
  let numPossibleConnections = tactics.length + 1

  return (
    <Formik
      validationSchema={schema}
      validateOnChange={isDirty}
      validateOnBlur={isDirty}
      initialValues={schema.cast(
        {
          ...schema.getDefault(),
          facebook_avg_daily_impressions: brandPlan.facebook_avg_daily_impressions,
          instagram_avg_daily_impressions: brandPlan.instagram_avg_daily_impressions,
          linkedin_avg_daily_impressions: brandPlan.linkedin_avg_daily_impressions,
          twitter_avg_daily_impressions: brandPlan.twitter_avg_daily_impressions,
          email_avg_open_rate: !brandPlan.email_avg_open_rate ? 0 : brandPlan.email_avg_open_rate,
          email_avg_click_through_rate: !brandPlan.email_avg_click_through_rate
            ? 0
            : brandPlan.email_avg_click_through_rate,
          seo_avg_daily_visits: brandPlan.seo_avg_daily_visits,
        },
        { assert: false },
      )}
      onSubmit={handleSubmit}
    >
      {({ submitForm, validateForm }) => (
        <Form>
          <MultiStepStepLayout
            title="Connect your channels"
            scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
            contentClassName={VirtualMarketerOnboardingStyles.content}
          >
            <StickyFooterProvider
              footer={
                <StickyFooter>
                  <FooterButtons>
                    <Button variant="tertiary" size="xl" onPress={onBack} frontIcon={ArrowLeft} aria-label="Back">
                      Back
                    </Button>
                    <Button
                      size="xl"
                      onPress={() => {
                        setIsDirty(true)
                        validateForm()
                        submitForm()
                      }}
                      endIcon={ArrowRight}
                      aria-label="Generate Content"
                      isDisabled={isGenerating}
                    >
                      {isGenerating ? 'Generating...' : 'Generate Content'}
                    </Button>
                  </FooterButtons>
                </StickyFooter>
              }
            >
              <div className={Styles.brandPlanCurrentVolume}>
                <TacticIntegrationTopBar
                  numConnectedAccounts={numConnectedAccounts}
                  numPossibleConnections={numPossibleConnections}
                />

                <TacticIntegrationList
                  tactics={tactics}
                  selectedBlogPlatform={selectedBlogPlatform}
                  selectedEmailPlatform={selectedEmailPlatform}
                />
              </div>
            </StickyFooterProvider>
          </MultiStepStepLayout>
        </Form>
      )}
    </Formik>
  )
}
